import React from 'react'
import { User, Heart, Loader2, Send } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import {
  accentColor, cardNickColor, cardPrimaryColor, cardSecondaryColor, contentBgColor, isValidHex
} from '../utils/profileColors'
import { backgroundImageStyle, backgroundOverlayStyle } from '../utils/profileBackground'
import { isValidCountryCode, formatAgo, formatOnProjectDays } from '../utils/profileFormat'
import { hapticImpact } from '../../../utils/haptic'
import TeamTag from '../../../components/TeamTag'
import AchievementShowcase from './AchievementShowcase'

/**
 * Шапка публичного профиля: аватар с рамкой, ник, онлайн/активность,
 * «на проекте», последняя ставка, лайк, контакт Telegram. Видна на любом
 * уровне доступа — контакт не подчиняется уровням видимости.
 */
const ProfileHeader = ({ profile, isOwner, onLike, isLikePending, onAchievementClick, onOpenAchievements }) => {
  const { t } = useTranslation()
  const colors = profile.colors
  // Картинка-фон карточки (null — нет загрузки, карточка красится в content_bg)
  const background = typeof profile.background?.url === 'string' ? profile.background : null

  // Ник и подписи hero берутся напрямую из настроек; автоматический основной цвет
  // нужен для нейтральных вложенных поверхностей и рамок на content_bg
  const primary = cardPrimaryColor(colors)
  const secondary = cardSecondaryColor(colors)
  const contentBg = contentBgColor(colors)
  const nick = cardNickColor(colors)
  const accent = accentColor(colors)
  const subtleSurface = `${primary}0D`
  const subtleBorder = `${primary}1F`
  // Рамка аватара: свой цвет или акцент темы
  const frame = isValidHex(colors?.avatar_frame) ? colors.avatar_frame : accent

  // Контакт Telegram: чужим приходит только при включённом переключателе;
  // владельцу — всегда при наличии ника (предпросмотр), прячем при show_telegram=false
  const telegram = (!isOwner || profile.show_telegram) ? profile.contacts?.telegram : null

  const name = `${profile.firstname || ''} ${profile.lastname || ''}`.trim() ||
    t('publicProfile.player', 'Игрок')

  // Уровень приходит всем и на любом уровне видимости (включая мини-карточку
  // скрытого профиля). На проде/rplayz ключа пока нет —
  // тогда бейдж не рисуем вовсе; при этом level === 0 валиден и показывается честно.
  const level = Number.isFinite(profile.level) ? profile.level : null
  // Прогресс до следующего уровня — только владельцу. Проверка по наличию ключа,
  // а не по истинности: 0% означает «только что взял уровень»
  const hasProgress = isOwner && typeof profile.level_progress_pct === 'number'
  const progressPct = hasProgress ? Math.min(100, Math.max(0, profile.level_progress_pct)) : 0

  const handleLike = () => {
    if (isOwner || isLikePending) return
    hapticImpact('light')
    onLike()
  }

  // url приходит готовый — собирать самому не нужно
  const handleTelegram = () => {
    if (!telegram?.url) return
    hapticImpact('light')
    const tg = window.Telegram?.WebApp
    if (tg?.openTelegramLink) {
      tg.openTelegramLink(telegram.url)
    } else {
      window.open(telegram.url, '_blank', 'noopener,noreferrer')
    }
  }

  // Кнопка лайка:
  // владелец — только счётчик; лайкнут — активное сердце (снять можно всегда);
  // can_like — пустое сердце; иначе — приглушенная кнопка, по тапу подсказка
  const likeButton = isOwner ? (
    <div className="flex items-center gap-1.5" style={{ color: secondary }}>
      <Heart size={16} />
      <span className="text-sm font-semibold tabular-nums">{profile.likes ?? 0}</span>
    </div>
  ) : (
    <button
      type="button"
      onClick={handleLike}
      disabled={isLikePending}
      className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 border transition-all active:scale-[0.98] disabled:opacity-60 ${
        !profile.liked_by_me && !profile.can_like ? 'opacity-50' : ''
      }`}
      style={{
        borderColor: profile.liked_by_me ? accent : subtleBorder,
        color: secondary,
        backgroundColor: profile.liked_by_me ? `${accent}14` : 'transparent'
      }}
      aria-label={t('publicProfile.likes', 'Лайки')}
    >
      {isLikePending ? (
        <Loader2 size={16} className="animate-spin" />
      ) : (
        <Heart
          size={16}
          className={profile.liked_by_me ? 'fill-current' : ''}
          style={{ color: profile.liked_by_me ? accent : secondary }}
        />
      )}
      <span className="text-sm font-semibold tabular-nums">{profile.likes ?? 0}</span>
    </button>
  )

  const infoRows = [
    profile.registered_ago != null
      ? { text: `${t('publicProfile.onProject', 'на проекте')} ${formatOnProjectDays(profile.registered_ago, t)}` }
      : null,
    profile.last_bet
      ? {
          text: `${t('publicProfile.lastBet', 'Последняя ставка')} ${formatAgo(profile.last_bet.ago, t)}`
        }
      : null
  ].filter(Boolean)

  return (
    <div
      className="relative overflow-hidden rounded-2xl border border-white/10 shadow-[inset_0_1px_0_rgba(255,255,255,0.07),0_10px_15px_-3px_rgba(0,0,0,0.25),0_4px_6px_-4px_rgba(0,0,0,0.25)] p-6"
      style={{ backgroundColor: contentBg, borderColor: subtleBorder }}
    >
      {/* Картинка-фон профиля: слои картинки и затемнения (оформление, видно
          на всех уровнях видимости); контент карточки позиционирован выше */}
      {background && (
        <>
          <div className="absolute pointer-events-none" style={backgroundImageStyle(background)} />
          <div className="absolute inset-0 pointer-events-none" style={backgroundOverlayStyle(background)} />
        </>
      )}

      {/* Мягкое свечение цвета профиля сверху (единственный тяжёлый blur карточки) */}
      <div
        className="pointer-events-none absolute -top-20 left-1/2 h-48 w-48 -translate-x-1/2 rounded-full opacity-20 blur-3xl"
        style={{ backgroundColor: frame }}
      />

      {/* Статус: онлайн — точка + «Онлайн»; оффлайн — как давно была
          последняя активность (active_ago, ключи publicProfile.ago.*) */}
      <div className="absolute left-4 top-4 flex items-center gap-1.5">
        <span
          className="w-2 h-2 rounded-full"
          style={
            profile.online
              ? { backgroundColor: accent, boxShadow: `0 0 8px ${accent}99` }
              : { backgroundColor: secondary }
          }
        />
        <span
          className="text-xs font-medium"
          style={{ color: secondary }}
        >
          {profile.online
            ? t('publicProfile.online', 'Онлайн')
            : (formatAgo(profile.active_ago, t) || t('publicProfile.offline', 'Оффлайн'))}
        </span>
      </div>

      {/* Флаг страны — в правом верхнем углу карточки.
          SVG с flagcdn.com (emoji-флаги не рендерятся на Windows); при ошибке загрузки прячем */}
      {profile.country && isValidCountryCode(profile.country) && (
        <img
          src={`https://flagcdn.com/${profile.country.toLowerCase()}.svg`}
          alt={profile.country}
          title={profile.country}
          loading="lazy"
          onError={(e) => { e.currentTarget.style.display = 'none' }}
          className="absolute right-4 top-4 block w-7 rounded-[3px] border border-white/20"
        />
      )}

      <div className="relative flex flex-col items-center">
        {/* Аватар с рамкой цвета профиля; онлайн-точка — бейдж слева сверху */}
        <div className="relative mb-3">
          <div
            className="w-24 h-24 rounded-full overflow-hidden border-2 p-0.5"
            style={{ borderColor: frame, boxShadow: `0 0 20px ${frame}55` }}
          >
          <div className="w-full h-full rounded-full overflow-hidden bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
            {profile.ava ? (
              <img
                src={profile.ava}
                alt={name}
                className="w-full h-full object-cover"
                onError={(e) => {
                  // Показываем заглушку вместо битой картинки
                  e.target.style.display = 'none'
                  if (e.target.nextElementSibling) {
                    e.target.nextElementSibling.style.display = 'block'
                  }
                }}
              />
            ) : null}
            {/* Заглушка, если аватара нет или он не загрузился */}
            <User size={40} className={`text-dark-primary ${profile.ava ? 'hidden' : ''}`} />
          </div>
          </div>

          {/* Бейдж уровня — таблетка на нижней кромке аватара. Ширина тянется
              под число (бывают четырёхзначные уровни), без анимации смены:
              уровень умеет и падать (XP сгорает), разница двух ответов не праздник */}
          {level !== null && (
            <div
              className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex items-center gap-1 whitespace-nowrap rounded-full border px-2.5 py-1"
              style={{ backgroundColor: contentBg, borderColor: frame, boxShadow: `0 0 12px ${frame}55` }}
              title={t('publicProfile.level', 'Уровень')}
            >
              <span className="text-[9px] font-bold uppercase leading-none tracking-wide" style={{ color: secondary }}>
                {t('publicProfile.levelShort', 'LVL')}
              </span>
              <span className="text-xs font-bold leading-none tabular-nums" style={{ color: secondary }}>
                {level}
              </span>
            </div>
          )}
        </div>

        {/* Банда владельца профиля — отдельной строкой над ником (на любом уровне
            видимости, как страна и оформление). В шапке места хватает, поэтому
            название не сужаем. `pl-1` гасит правый отступ бейджа, который нужен
            ему только когда он стоит вплотную перед ником в одну строку */}
        {profile.team && (
          <div className="flex justify-center pl-1">
            <TeamTag team={profile.team} color={accent} className="text-sm font-semibold" />
          </div>
        )}

        <h3 className="text-xl font-bold text-center" style={{ color: nick }}>
          {name}
        </h3>

        {/* Прогресс до следующего уровня — только в своём профиле (в чужом
            ключа нет вовсе, нулевую шкалу не рисуем) */}
        {hasProgress && (
          <div className="mt-3 w-full max-w-[220px]">
            <div className="mb-1 flex items-center justify-between text-[11px]" style={{ color: secondary }}>
              <span>{t('publicProfile.levelNext', 'До {{level}} уровня', { level: (level ?? 0) + 1 })}</span>
              <span className="font-semibold tabular-nums">{progressPct}%</span>
            </div>
            <div
              className="h-1.5 overflow-hidden rounded-full border"
              style={{ backgroundColor: subtleSurface, borderColor: subtleBorder }}
            >
              <div
                className="h-full rounded-full transition-all"
                style={{ width: `${progressPct}%`, backgroundColor: accent, boxShadow: `0 0 8px ${accent}66` }}
              />
            </div>
          </div>
        )}

        <div className="mt-2">
          {likeButton}
        </div>
      </div>

      {/* Статус: «на проекте», последняя ставка (онлайн/оффлайн — бейдж в углу) */}
      {infoRows.length > 0 && (
        <div className="relative mt-4 space-y-1.5 border-t pt-4" style={{ borderColor: subtleBorder }}>
          {infoRows.map((row, index) => (
            <div key={index} className="flex justify-center">
              <span className="text-sm text-center" style={{ color: secondary }}>
                {row.text}
              </span>
            </div>
          ))}
        </div>
      )}

      <AchievementShowcase
        achievements={profile.achievements}
        accent={accent}
        primary={primary}
        secondary={secondary}
        onAchievementClick={onAchievementClick}
        onOpenAll={onOpenAchievements}
      />

      {/* Контакт Telegram — отдельная настройка игрока: виден на любом уровне
          видимости, в том числе в мини-карточке скрытого профиля */}
      {telegram && (
        <div
          className={infoRows.length > 0 ? 'relative mt-3' : 'relative mt-4 border-t pt-4'}
          style={infoRows.length > 0 ? undefined : { borderColor: subtleBorder }}
        >
          <button
            type="button"
            onClick={handleTelegram}
            className="w-full flex items-center justify-center gap-2 rounded-xl border px-4 py-2.5 transition-all active:scale-[0.98]"
            style={{
              borderColor: `${accent}55`,
              color: secondary,
              backgroundColor: `${accent}14`
            }}
            aria-label={t('publicProfile.telegramContact', 'Написать в Telegram')}
          >
            <Send size={16} className="shrink-0" style={{ color: accent }} />
            <span className="text-sm font-semibold truncate">@{telegram.username}</span>
          </button>
        </div>
      )}
    </div>
  )
}

export default ProfileHeader
